/*
|--------------------------------------------------------------------------
| Event Wiring / Interaction Behavior
|--------------------------------------------------------------------------
|
| This file contains:
| - Event handlers
| - Click bindings
| - Modal behavior
| - Interaction logic
|
| These functions should:
| - Accept $context
| - Use specific selectors
| - Be safely re-bindable (.off().on())
|
| This file should NOT contain:
| - Utility functions
| - Low-level DOM helpers
| - Framework infrastructure
|
| Think: user interaction and behavioral wiring.
|
*/
function BindEvents($context: JQuery<any>): void {
    BindEmailButtons($context);
    BindNestedModals($context);
    BindUserActiveDirectoryButtons($context);
}

function BindEmailButtons($context: JQuery<any>): void {

    //#region Attachment Label Helper
    //update label with file count
    function updateAttachmentInputLabel($input: JQuery<any>): void {
        var $list = $input.siblings('ul[name="Email_Attachments_List"]');
        var count = $list.find('li').length;

        // Use the existing label from your markup
        var $label = $input.siblings('label[name="Email_Attachments_Label"]');

        if (!$label.length) {
            // fallback, just in case
            $label = $("<span class='file-count-label ms-2 text-muted'></span>");
            $input.after($label);
        }

        if (count === 0) {
            $label.text("No files chosen");
        } else if (count === 1) {
            $label.text("1 file chosen");
        } else {
            $label.text(`${count} files chosen`);
        }
    }
    //#endregion Attachment Label Helper

    //#region File Input Setup
    // hide the file input visually
    $context.findInGrid('input[name="Email_Attachments"]').css({ position: 'absolute', left: '-9999px' });

    // trigger file input via button
    $context.findInGrid('[name="Email_Attachments_Button"]').off('click').on('click', function (this: HTMLElement): void {
        $(this).siblings('input[name="Email_Attachments"]').click();
    });
    //#endregion File Input Setup

    //#region File Add
    $context.findInGrid('input[name="Email_Attachments"]').off('change.attach').on('change.attach', function (this: HTMLInputElement): void {
        var $input = $(this);
        var $list = $input.siblings('ul[name="Email_Attachments_List"]');
        var files = Array.from(this.files || []);

        files.forEach(function (f: File): void {
            var $li = $('<li class="list-group-item d-flex align-items-center"></li>');

            // trash button first
            var $btn = $('<button type="button" class="btn btn-danger btn-sm me-2"><i class="bi bi-trash3"></i></button>');
            $btn.on('click', function (): void {
                $li.remove();
                updateAttachmentInputLabel($input);
                updateInputFiles($input);
            });
            $li.append($btn);

            // then file name
            $li.append(`<span>${f.name}</span>`);

            $li.data('file', f);
            $list.append($li);
        });

        updateAttachmentInputLabel($input);
        updateInputFiles($input);
        $input.val(""); // clear input so same file can be re-added
    });
    //#endregion File Add

    //#region Update Input Files
    function updateInputFiles($input: JQuery<any>): void {
        if (typeof DataTransfer !== "undefined") {
            var dt = new DataTransfer();
            $input.siblings('ul[name="Email_Attachments_List"]').find('li').each(function (this: HTMLElement): void {
                var f = $(this).data('file');
                if (f) dt.items.add(f);
            });
            ($input[0] as HTMLInputElement).files = dt.files;
        }
    }
    //#endregion Update Input Files


    //#region Send Email
    $context.findInGrid('button[name="btnSendEmailConfirm"]').off("click").on('click', function (event: any): void {
        const $el = $(this);
        const $modal = $el.closest('.modal');

        var formData = new FormData();

        // append text inputs
        $modal.find(':input[name]').each(function (): void {
            if (!(this instanceof HTMLInputElement || this instanceof HTMLTextAreaElement)) return;

            if (this.type !== "file") {
                formData.append(this.name, $(this).val() as string);
            }
        });

        // append files
        $modal.find('.attachmentList li').each(function (this: HTMLElement): void {
            var file = $(this).data('file');
            if (file) {
                formData.append("Email_Attachments", file);
            }
        });

        $.ajax({
            type: 'POST',
            url: `${window.appRoot}Email/SendEmail`,
            data: formData,
            processData: false,
            contentType: false,
            success: function (response: AjaxSaveDetailsResult): void {
                if (response.Success) {
                    getModal($modal).hide();
                } else {
                    appendMessagePartial($modal, response.SaveMessageView);
                }
            },
            error: function (xhr, status, error) {
                console.error(error);
            }
        });
    });
    //#endregion Send Email
}

function BindNestedModals($context: JQuery<any>): void {
    //Bind Email Modal Open Buttons to Allow Modal to work within Modal
    $context.findInGrid('[data-nested-modal-target]').off('click').on('click', function (this: HTMLElement, event: any): void {
        event.preventDefault();

        var targetSelector = $(this).attr('data-nested-modal-target');
        if (!targetSelector) return;

        var $nestedModal = $(targetSelector as string);
        var $parentModal = $(this).closest('.modal.show');

        const modalEl = $nestedModal[0];
        if (!modalEl) return;

        // Always dispose existing instance (safe reset)
        var existing = bootstrap.Modal.getInstance(modalEl);
        existing?.dispose();

        if ($parentModal.length) {
            $nestedModal.addClass('second-modal');

            // Create new instance with backdrop disabled
            var modalInstance = new bootstrap.Modal(modalEl, { backdrop: false, keyboard: true });

            // Instead of appending to body:
            $nestedModal.parent().append(
                $('<div class="modal-backdrop fade show second-backdrop"></div>').css('z-index', 1065)
            );

            // Cleanup when the modal closes
            $nestedModal.one('hidden.bs.modal', function (): void {
                $('.modal-backdrop.second-backdrop').remove();
                $nestedModal.removeClass('second-modal');
            });

            modalInstance.show();
        } else {
            // Normal modal
            var modalInstance = new bootstrap.Modal(modalEl);
            modalInstance.show();
        }
    });
}

function BindUserActiveDirectoryButtons($context: JQuery<any>): void {
    $context.findInGrid('#btnADSearch').off('click').on('click', function (): void {
        var searchTxt = $context.findInGrid('#adSearchText').val();

        $.ajax({
            url: '/User/ADUserSearch',
            type: 'POST',
            data: { searchTxt: searchTxt },
            success: function (html: any): void {
                var $adResultsContainer = $context.findInGrid('#adResultsContainer');
                $adResultsContainer.html(html);
                BindCommon($adResultsContainer);
            },
            error: function (xhr, status, error: any): void {
                console.error('AD search failed:', error);
            }
        });
    });

    $context.findInGrid('input[name="adSearchText"]').off('keydown').on('keydown', function (event: JQuery.KeyDownEvent): void {
        if (event.key === 'Enter') {
            event.preventDefault();

            $context.findInGrid('#btnADSearch').click();
        }
    });

    $context.findInGrid('.ad-select-user').off('click').on('click', function (): void {
        var self = $(this);
        var userName = self.data('username');
        var email = self.data('email');
        var display = self.data('display');

        var $root = $(document);

        $root.find('input[name="UserName"]').val(userName);
        $root.find('input[name="Email"]').val(email);
        $root.find('input[name="DisplayName"]').val(display);

        var modal = getModal($('#adUserLookupModal'));
        modal.hide();
    });
}
